import { clsx } from "clsx";
import {
  createRef,
  FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Button } from "../Button/Button";
import { Icon } from "../Icons/Icons";
import { LoadingPlaceholder } from "../LoadingPlaceholder/LoadingPlaceholder";
import { MultiSegmentControl } from "../SegmentControl/MultiSegmentControl";
import { Tooltip } from "../Tooltip/Tooltip";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./NotificationBar.css";
import {
  NotificationCard,
  type NotificationCardData,
  type NotificationTypeConfig,
} from "./NotificationCard";
import { NotificationCounter } from "./NotificationCounter";

type TabValue = "time" | "type" | "status";

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: "time", label: "By time" },
  { value: "type", label: "By type" },
  { value: "status", label: "By status" },
];

const NOTIFICATION_TRANSITION_TIMEOUT = 300;

type TransitionNodeRef = RefObject<HTMLDivElement>;

export interface NotificationBarProps {
  /** Whether the bar is visible */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** Notification data */
  notifications: NotificationCardData[];
  /** Icon + label config per notification type (icon, bgColor, iconColor, label) */
  typeConfig?: Partial<Record<string, NotificationTypeConfig>>;
  /** Status group labels — defaults to { UNREAD: "Unread", READ: "Read" } */
  statusLabels?: Record<string, string>;
  /** Fired when a notification card is clicked */
  onNotificationClick?: (notification: NotificationCardData) => void;
  /** Fired when a single notification is marked as read via the checkmark button */
  onMarkRead?: (notification: NotificationCardData) => Promise<void> | void;
  /** Fired when a single read notification is marked as unread via the bell button */
  onMarkUnread?: (notification: NotificationCardData) => Promise<void> | void;
  /** Fired when "Mark all as read" is clicked */
  onMarkAllRead?: () => void;
  /** Loading state for the "Mark all as read" action */
  isMarkAllReadLoading?: boolean;
  /** Whether more pages are available */
  hasMore?: boolean;
  /** Whether the next page is currently being fetched */
  isLoadingMore?: boolean;
  /** Callback to fetch the next page */
  onLoadMore?: () => void;
  /** Initial list loading state */
  isLoading?: boolean;
  /** Additional className */
  className?: string;
  /** Render as static (inline) instead of floating */
  isStatic?: boolean;
}

const DEFAULT_STATUS_LABELS: Record<string, string> = {
  UNREAD: "Unread",
  READ: "Read",
};

/**
 * NotificationBar — A sidebar panel that lists notifications
 * with "By time" / "By type" / "By status" tabs.
 * Designed to be placed inside a react-resizable-panels Panel.
 */
export const NotificationBar: FC<NotificationBarProps> = ({
  isOpen,
  onClose,
  notifications,
  typeConfig = {},
  statusLabels = DEFAULT_STATUS_LABELS,
  onNotificationClick,
  onMarkRead,
  onMarkUnread,
  onMarkAllRead,
  isMarkAllReadLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  isLoading = false,
  className,
  isStatic = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>("time");
  const [hideRead, setHideRead] = useState(false);
  const [collapsedTypeGroups, setCollapsedTypeGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const [collapsedStatusGroups, setCollapsedStatusGroups] = useState<
    Set<string>
  >(() => new Set(["READ"]));
  const contentRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cardNodeRefs = useRef<Map<string, TransitionNodeRef>>(new Map());
  const sectionNodeRefs = useRef<Map<string, TransitionNodeRef>>(new Map());

  useEffect(() => {
    if (
      !contentRef.current ||
      !sentinelRef.current ||
      !hasMore ||
      isLoadingMore
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          onLoadMore?.();
        }
      },
      {
        root: contentRef.current,
        threshold: 0.1,
      },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.status === "UNREAD").length,
    [notifications],
  );

  const readCount = useMemo(
    () => notifications.filter((n) => n.status !== "UNREAD").length,
    [notifications],
  );

  const visibleNotifications = useMemo(
    () =>
      hideRead
        ? notifications.filter((n) => n.status === "UNREAD")
        : notifications,
    [hideRead, notifications],
  );

  const toggleTypeGroup = useCallback((type: string) => {
    setCollapsedTypeGroups((previous) => {
      const next = new Set(previous);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const toggleStatusGroup = useCallback((status: string) => {
    setCollapsedStatusGroups((previous) => {
      const next = new Set(previous);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const getCardNodeRef = useCallback((id: string) => {
    const existingRef = cardNodeRefs.current.get(id);
    if (existingRef) return existingRef;

    const nodeRef = createRef<HTMLDivElement>() as TransitionNodeRef;
    cardNodeRefs.current.set(id, nodeRef);
    return nodeRef;
  }, []);

  const getSectionNodeRef = useCallback((key: string) => {
    const existingRef = sectionNodeRefs.current.get(key);
    if (existingRef) return existingRef;

    const nodeRef = createRef<HTMLDivElement>() as TransitionNodeRef;
    sectionNodeRefs.current.set(key, nodeRef);
    return nodeRef;
  }, []);

  // ── Grouping logic ──

  /** By time: newest first */
  const byTime = useMemo(
    () =>
      [...visibleNotifications].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [visibleNotifications],
  );

  /** By type: grouped by notificationType */
  const byType = useMemo(() => {
    const groups: Record<string, NotificationCardData[]> = {};
    for (const n of visibleNotifications) {
      const key = n.notificationType;
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }
    return groups;
  }, [visibleNotifications]);

  /** By status: Unread first, then Read */
  const byStatus = useMemo(() => {
    const unread = visibleNotifications
      .filter((n) => n.status === "UNREAD")
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    const read = visibleNotifications
      .filter((n) => n.status !== "UNREAD")
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    return { unread, read };
  }, [visibleNotifications]);

  // ── Render helpers ──

  const getGroupContentId = (kind: string, key: string) =>
    `notification-bar-${kind}-${key.replace(/[^a-z0-9_-]/gi, "-")}`;

  const renderGroupHeader = ({
    label,
    isCollapsed,
    onToggle,
    counter,
    contentId,
  }: {
    label: string;
    isCollapsed: boolean;
    onToggle: () => void;
    counter?: number;
    contentId: string;
  }) => (
    <button
      type="button"
      className="notification-bar__group-header"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      aria-controls={contentId}
    >
      <span className="notification-bar__group-title-wrap">
        <Heading level={3} className="notification-bar__group-label">
          {label}
        </Heading>
        <Icon
          name="down"
          size="xs"
          className={clsx(
            "notification-bar__group-chevron",
            isCollapsed && "notification-bar__group-chevron--collapsed",
          )}
        />
      </span>
      {counter !== undefined && counter > 0 && (
        <NotificationCounter
          count={counter}
          className="notification-bar__group-counter"
        />
      )}
    </button>
  );

  const renderCard = (notification: NotificationCardData) => (
    <NotificationCard
      notification={notification}
      typeConfig={typeConfig[notification.notificationType]}
      onClick={() => onNotificationClick?.(notification)}
      onMarkRead={onMarkRead}
      onMarkUnread={onMarkUnread}
    />
  );

  const renderAnimatedCard = (notification: NotificationCardData) => {
    const nodeRef = getCardNodeRef(notification.id);

    return (
      <CSSTransition
        key={notification.id}
        nodeRef={nodeRef}
        timeout={NOTIFICATION_TRANSITION_TIMEOUT}
        classNames="notification-bar-card"
      >
        <div ref={nodeRef} className="notification-bar-card">
          {renderCard(notification)}
        </div>
      </CSSTransition>
    );
  };

  const renderAnimatedSection = (key: string, children: ReactNode) => {
    const nodeRef = getSectionNodeRef(key);

    return (
      <CSSTransition
        key={key}
        nodeRef={nodeRef}
        timeout={NOTIFICATION_TRANSITION_TIMEOUT}
        classNames="notification-bar-section"
      >
        <div ref={nodeRef} className="notification-bar-section">
          {children}
        </div>
      </CSSTransition>
    );
  };

  const renderByTime = () => (
    <TransitionGroup key="time" className="notification-bar__list">
      {byTime.map(renderAnimatedCard)}
    </TransitionGroup>
  );

  const renderByType = () => (
    <TransitionGroup key="type" className="notification-bar__list">
      {Object.entries(byType).map(([type, items]) => {
        const contentId = getGroupContentId("type", type);
        const isCollapsed = collapsedTypeGroups.has(type);
        const unreadInGroup = items.filter((n) => n.status === "UNREAD").length;

        return renderAnimatedSection(
          `type-${type}`,
          <div className="notification-bar__group">
            {renderGroupHeader({
              label: typeConfig[type]?.label ?? type,
              isCollapsed,
              onToggle: () => toggleTypeGroup(type),
              counter: unreadInGroup,
              contentId,
            })}
            <div
              id={contentId}
              className={clsx(
                "notification-bar__group-content",
                isCollapsed && "notification-bar__group-content--collapsed",
              )}
            >
              <TransitionGroup className="notification-bar__group-content-inner">
                {items.map(renderAnimatedCard)}
              </TransitionGroup>
            </div>
          </div>,
        );
      })}
    </TransitionGroup>
  );

  const renderByStatus = () => {
    const groups = [
      {
        key: "UNREAD",
        label: statusLabels.UNREAD ?? "Unread",
        items: byStatus.unread,
        counter: byStatus.unread.length,
      },
      {
        key: "READ",
        label: statusLabels.READ ?? "Read",
        items: byStatus.read,
        counter: 0,
      },
    ];

    return (
      <TransitionGroup key="status" className="notification-bar__list">
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => {
            const contentId = getGroupContentId("status", group.key);
            const isCollapsed = collapsedStatusGroups.has(group.key);

            return renderAnimatedSection(
              `status-${group.key}`,
              <div className="notification-bar__group">
                {renderGroupHeader({
                  label: group.label,
                  isCollapsed,
                  onToggle: () => toggleStatusGroup(group.key),
                  counter: group.counter,
                  contentId,
                })}
                <div
                  id={contentId}
                  className={clsx(
                    "notification-bar__group-content",
                    isCollapsed && "notification-bar__group-content--collapsed",
                  )}
                >
                  <TransitionGroup className="notification-bar__group-content-inner">
                    {group.items.map(renderAnimatedCard)}
                  </TransitionGroup>
                </div>
              </div>,
            );
          })}
      </TransitionGroup>
    );
  };

  const renderContent = () => {
    if (isLoading && notifications.length === 0) {
      return (
        <div className="notification-bar__loading-state">
          <LoadingPlaceholder width={24} height={24} />
        </div>
      );
    }

    if (visibleNotifications.length === 0) {
      return (
        <div className="notification-bar__empty">
          <Body size="sm" className="notification-bar__empty-text">
            {hideRead ? "No unread notifications" : "No notifications"}
          </Body>
        </div>
      );
    }

    switch (activeTab) {
      case "type":
        return renderByType();
      case "status":
        return renderByStatus();
      default:
        return renderByTime();
    }
  };

  return (
    <div
      className={clsx(
        "notification-bar",
        isOpen ? "notification-bar--open" : "notification-bar--closed",
        isStatic && "notification-bar--static",
        className,
      )}
    >
      {isOpen && (
        <>
          {/* ── Header ── */}
          <div className="notification-bar__header">
            {/* Row 1: close icon right-aligned */}
            <div className="notification-bar__header-close-row">
              <Tooltip content="Close" placement="bottom-end">
                <button
                  onClick={onClose}
                  className="notification-bar__close"
                  aria-label="Close notifications"
                  type="button"
                >
                  <Icon name="close" />
                </button>
              </Tooltip>
            </div>

            {/* Row 2: title + Mark all as read */}
            <div className="notification-bar__header-title-row">
              <Heading level={2} className="notification-bar__title">
                Notifications
              </Heading>
              {(readCount > 0 || unreadCount > 0) && (
                <div className="notification-bar__header-actions">
                  {readCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Icon name={hideRead ? "eye" : "closed-eye"} />}
                      onClick={() => setHideRead((previous) => !previous)}
                      className="notification-bar__visibility-toggle"
                      aria-pressed={hideRead}
                    >
                      {hideRead ? "Show read" : "Hide read"}
                    </Button>
                  )}
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Icon name="check" size="xs" />}
                      loading={isMarkAllReadLoading}
                      onClick={onMarkAllRead}
                      className="notification-bar__mark-read"
                    >
                      Mark all as read
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Row 3: segment control full-width */}
            <MultiSegmentControl<TabValue>
              size="small"
              fullWidth
              value={activeTab}
              options={TAB_OPTIONS}
              onChange={setActiveTab}
            />
          </div>

          {/* ── Scrollable list ── */}
          <div ref={contentRef} className="notification-bar__content">
            {renderContent()}
            {isLoadingMore && notifications.length > 0 && (
              <div className="notification-bar__bottom-loader">
                <LoadingPlaceholder width={24} height={24} className="mb-2" />
              </div>
            )}
            {hasMore && (
              <div ref={sentinelRef} className="notification-bar__sentinel" />
            )}
          </div>
        </>
      )}
    </div>
  );
};
